"use client";

import { useEffect } from "react";
import { allowAnalyticsWithoutAsking, applyStoredConsent, recordConsentDecision } from "@/lib/consent/apply";
import { clearVendorCookies, readConsentCookie } from "@/lib/consent/cookie";
import { isConsentAskedHere, isConsentRequiredHere } from "@/lib/consent/region";
import { useConsentStore } from "@/store/consentStore";

/**
 * Resolves what this visitor has already agreed to, and starts whatever that
 * allows.
 *
 * Mounted above `Providers` so it runs on every route regardless of the app
 * shell, and reads the cookie in an effect rather than through `next/headers`
 * so pages stay statically prerendered.
 *
 * Until this resolves the store says `unknown` and nothing non-essential loads.
 */
export function ConsentBootstrap() {
  useEffect(() => {
    const stored = readConsentCookie();
    if (stored) {
      applyStoredConsent(stored);
      return;
    }

    if (!isConsentAskedHere()) {
      allowAnalyticsWithoutAsking();
      return;
    }

    let abandoned = false;
    // Mirrors the lookup's own fallback for a throw raised before its try block,
    // which would otherwise strand the store on `unknown` — where no banner
    // renders and no tag loads, so the visitor is neither asked nor measured.
    void isConsentRequiredHere()
      .catch(() => false)
      .then((required) => {
        if (abandoned) return;

        if (required) {
          // Everyone who visited before the banner shipped still carries `_ga`.
          // Leaving it in place would contradict a refusal that has not been made
          // yet. Only on the ask path: outside the EU we grant immediately, so
          // clearing there would reset identities for no reason.
          clearVendorCookies();
          useConsentStore.getState().setAsking();
          return;
        }

        // Nobody placed outside the EU is asked, and nobody we failed to place is
        // either. The outcome is still recorded, and the receipt carries the country
        // the edge stamped on that POST — null when it could not place them — so an
        // audit separates "they agreed", "never in scope", and "never established".
        recordConsentDecision({ analytics: true, basis: "region" });
      });

    return () => {
      abandoned = true;
    };
  }, []);

  return null;
}
